import React from 'react';
import {
  Github,
  Database,
  Server,
  Clock,
  ShieldCheck,
  FileText,
  ArrowDown,
  Lock,
  Box as BoxIcon,
  CheckCircle2,
  Bitcoin,
} from 'lucide-react';

import {
  Anchor,
  Badge,
  Box,
  Container,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

function SectionLabel({ label }) {
  return (
    <Badge variant="light" color="gray" radius="xl" size="md">
      {label}
    </Badge>
  );
}

function SourceCard({ icon, iconColor, title, description, badge }) {
  return (
    <Paper radius="md" p="lg" withBorder>
      <Stack gap="md">
        <Group>
          <ThemeIcon variant="light" color={iconColor} size={52}>
            {icon}
          </ThemeIcon>
          <Title order={3} fz="1.25rem">
            {title}
          </Title>
        </Group>
        <Text fz="sm" c="dimmed">
          {description}
        </Text>
        <Badge variant="outline" color="green" leftSection={<ShieldCheck size={14} />}>
          {badge}
        </Badge>
      </Stack>
    </Paper>
  );
}

function EngineBox({ icon, iconColor, title, subtitle }) {
  return (
    <Paper withBorder radius="md" p="md" w="100%">
      <Stack gap={6} align="center" ta="center">
        <ThemeIcon variant="light" color={iconColor} size="lg" radius="xl">
          {icon}
        </ThemeIcon>
        <Text fw={700}>{title}</Text>
        <Text c="dimmed" fz="sm">
          {subtitle}
        </Text>
      </Stack>
    </Paper>
  );
}

export default function ArchitectureInfographic() {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme('light');

  return (
    <Box mih="100vh" py={60}>
      <Container size="lg">
        <Stack gap="xl">
          <Stack align="center" ta="center" gap="sm">
            <Title order={1} fz="2.5rem" fw={700}>
              {t('Zero-Knowledge Timestamp Architecture')}
            </Title>
            <Text c="dimmed" size="lg" maw={700}>
              {t('How we mathematically secure your prior art on the Bitcoin blockchain using a lean, single-container deployment and secure Shadow Repos.')}
            </Text>
          </Stack>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
            <Stack gap="md">
              <SectionLabel label={t('1. The Source')} />

              <SourceCard
                icon={<BoxIcon size={24} />}
                iconColor="blue"
                title={t('Standard Mode')}
                description={t('GitHub App reads the ') + t('HEAD commit hash of the monitored repository automatically.')}
                badge={t('Read-Only Access')}
              />

              <SourceCard
                icon={<Lock size={24} />}
                iconColor="grape"
                title={t('Enterprise ZK')}
                description={t('A 12-line GitHub Action runs on your servers and PUSHES the commit hash to our API.')}
                badge={t('Zero Code Access')}
              />
            </Stack>

            <Stack gap="md">
              <SectionLabel label={t('2. The Monolith Engine')} />

              <Paper
                withBorder
                radius="lg"
                p="lg"
                bg={colorScheme === 'dark' ? 'dark.7' : 'gray.0'}
              >
                <Stack gap="lg">
                  <Group justify="center">
                    <ThemeIcon variant="light" color="teal" size="xl" radius="xl">
                      <Server size={24} />
                    </ThemeIcon>
                    <Title order={2} fz="1.5rem">
                      {t('Docker Server')}
                    </Title>
                  </Group>

                  <SimpleGrid cols={2} spacing="sm">
                    <EngineBox
                      icon={<Server size={20} />}
                      iconColor="blue"
                      title={t('aiohttp')}
                      subtitle={t('Webhooks')}
                    />
                    <EngineBox
                      icon={<Database size={20} />}
                      iconColor="grape"
                      title={t('ValKey')}
                      subtitle={t('Hash Queue')}
                    />
                  </SimpleGrid>

                  <Paper withBorder p="md" radius="md">
                    <Stack gap="sm">
                      <Group gap="xs">
                        <Clock size={18} />
                        <Text fw={700}>
                          {t('Midnight Cron Worker')}
                        </Text>
                      </Group>
                      <List
                        spacing="xs"
                        icon={
                          <ThemeIcon size={16} variant="light" color="teal" radius="xl">
                            <CheckCircle2 size={10} />
                          </ThemeIcon>
                        }
                      >
                        <List.Item>
                          {t('Groups all pending hashes')}
                        </List.Item>
                        <List.Item>
                          {t('Builds Merkle Tree natively')}
                        </List.Item>
                        <List.Item>
                          {t('Connects to OTS Calendars')}
                        </List.Item>
                      </List>
                    </Stack>
                  </Paper>
                </Stack>
              </Paper>
            </Stack>

            <Stack gap="md" h="100%">
              <SectionLabel label={t('3. The Destination')} />

              <Paper radius="md" p="lg" withBorder>
                <Group mb="md">
                  <ThemeIcon variant="light" color="orange" size={52}>
                    <Bitcoin size={24} />
                  </ThemeIcon>
                  <Title order={3} fz="1.25rem">
                    {t('Bitcoin Network')}
                  </Title>
                </Group>

                <Text fz="sm" c="dimmed">
                  {t("The ")}
                  <Anchor
                    fz="sm"
                    c="dimmed"
                    href="https://opentimestamps.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                  >
                    {t('OpenTimestamps')}
                  </Anchor>

                  {t(' protocol anchors the Merkle root of the daily batch into a Bitcoin block.')}
                </Text>
              </Paper>

              <Group justify="center">
                <ArrowDown size={28} />
              </Group>

              <Paper radius="md" p="lg" withBorder>
                <Stack gap="md">
                  <Group>
                    <ThemeIcon variant="light" color="teal" size={52}>
                      <Github size={24} />
                    </ThemeIcon>
                    <Title order={3} fz="1.25rem">
                      {t('Timestamp Branch / Private Shadow Repo')}
                    </Title>
                  </Group>

                  <Text fz="sm" c="dimmed">
                    {t("Our bot directly pushes the cryptographic proofs to the client's repository.")}
                  </Text>

                  <Paper withBorder p="sm" radius="md">
                    <Text ff="monospace" fz="sm">
                      /timestamps/2026-07-30.txt
                    </Text>
                  </Paper>
                  <Paper withBorder p="sm" radius="md">
                    <Group justify="space-between" align="center">
                      <Text ff="monospace" fz="sm">
                        /timestamps/2026-07-30.txt.ots
                      </Text>
                      <Badge variant="filled" color="teal">
                        {t('Proof')}
                      </Badge>
                    </Group>
                  </Paper>
                  <Paper withBorder p="sm" radius="md">
                    <Group justify="space-between" align="center">
                      <Text ff="monospace" fz="sm">
                        audit_log.csv
                      </Text>
                      <Badge variant="filled" color="blue">
                        {t('Ledger')}
                      </Badge>
                    </Group>
                  </Paper>
                </Stack>
              </Paper>
            </Stack>
          </SimpleGrid>

          <Paper withBorder p="xl" radius="xl" bg={colorScheme === 'dark' ? 'dark.7' : 'gray.0'}>
            <Stack gap="md">
              <Title order={3} fz="1.25rem">
                {t('Daily Operations Model')}
              </Title>
              <List
                spacing="md"
                icon={
                  <ThemeIcon size={20} variant="light" color="teal" radius="xl">
                    <CheckCircle2 size={12} />
                  </ThemeIcon>
                }
              >
                <List.Item>
                  {t('Once a day, we start an OpenTimestamps stamp for all pending hashes.')}
                </List.Item>
                <List.Item>
                  {t('It takes several hours until the transaction is confirmed in the Bitcoin blockchain.')}
                </List.Item>
                <List.Item>
                  {t('Once confirmed, we write the timestamp into your repository and show it in your status badge.')}
                </List.Item>
              </List>
            </Stack>
          </Paper>

          <Paper withBorder p="xl" radius="xl" bg={colorScheme === 'dark' ? 'dark.7' : 'gray.0'}>
            <Stack gap="md">
              <Title order={3} fz="1.25rem">
                {t('Status Badges')}
              </Title>
              <Text c="dimmed">
                {t('Easy access to status information via status badges that can be embedded directly in your README.md.')}
              </Text>

              <Group gap="sm" wrap="wrap">
                <Group gap={0} wrap="nowrap">
                  <Badge radius="sm" color="dark" variant="filled">
                    {t('Status')}
                  </Badge>
                  <Badge radius="sm" color="blue" variant="filled">
                    {t('Pending Bitcoin Block')}
                  </Badge>
                </Group>

                <Group gap={0} wrap="nowrap">
                  <Badge radius="sm" color="dark" variant="filled">
                    {t('Prior Art Proofs')}
                  </Badge>
                  <Badge radius="sm" color="teal" variant="filled">
                    {t('412 Commits Sealed')}
                  </Badge>
                </Group>
              </Group>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
